"use client";

import { Suspense, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import BankAccountCard from "@/components/customui/BankAccountCard";
import BankAccountFilter from "@/components/customui/bankaccountfilter";

type BankAccount = {
  id: number;
  bankLogo: string;
  bankName: string;
  accountName: string;
  minBalance?: number | null;
  minIncome?: number | null;
  accountType?: string;
  features: string[];
  offers?: string[];
  accountUrl?: string;
};

type SelectedFilters = {
  banks: string[];
  accounttypes: string[];
  salary: string;
};

const normalize = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const FEATURE_PRESET_TITLES: Record<string, string> = {
  "best-salary-account-deal": "Best Salary Account Deal",
  "best-current-account": "Best Current Account",
  "best-deposit-account": "Best Deposit Account",
};

function BankAccountFeaturesContent() {
  const searchParams = useSearchParams();
  const featureParam = searchParams.get("feature") || "";

  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [selectedFeature, setSelectedFeature] = useState<string>("all");
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    banks: [],
    accounttypes: [],
    salary: "0",
  });

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedFilters.banks.length > 0) {
          params.append("banks", selectedFilters.banks.join(","));
        }
        if (selectedFilters.accounttypes.length > 0) {
          params.append("accounttypes", selectedFilters.accounttypes.join(","));
        }
        if (selectedFilters.salary && selectedFilters.salary !== "0") {
          params.append("salary", selectedFilters.salary);
        }

        const res = await fetch(`/api/bankaccounts?${params.toString()}`);
        const data: BankAccount[] = await res.json();
        setAccounts(data);
      } catch {
        setAccounts([]);
      }
    };

    fetchAccounts();
  }, [selectedFilters]);

  useEffect(() => {
    if (!featureParam) {
      setSelectedFeature("all");
      return;
    }
    setSelectedFeature(decodeURIComponent(featureParam));
  }, [featureParam]);

  const featureOptions = useMemo(() => {
    const features = Array.from(
      new Set(
        accounts
          .flatMap((account) => account.features || [])
          .map((feature) => feature.trim())
          .filter(Boolean)
      )
    );
    return features.sort((a, b) => a.localeCompare(b));
  }, [accounts]);

  const filteredAccounts = useMemo(() => {
    if (selectedFeature === "all") return accounts;

    const normalizedSelected = normalize(selectedFeature);

    if (normalizedSelected === normalize("best-salary-account-deal")) {
      return accounts.filter(
        (account) =>
          normalize(account.accountType || "").includes("salary") ||
          normalize(account.accountName || "").includes("salary") ||
          (account.features || []).some((f) => normalize(f).includes("salary"))
      );
    }

    if (normalizedSelected === normalize("best-current-account")) {
      return accounts.filter(
        (account) =>
          normalize(account.accountType || "").includes("current") ||
          normalize(account.accountName || "").includes("current") ||
          (account.features || []).some((f) => normalize(f).includes("current"))
      );
    }

    if (normalizedSelected === normalize("best-deposit-account")) {
      return accounts.filter(
        (account) =>
          normalize(account.accountType || "").includes("deposit") ||
          normalize(account.accountName || "").includes("deposit") ||
          (account.features || []).some((f) => normalize(f).includes("deposit"))
      );
    }

    return accounts.filter((account) =>
      (account.features || []).some((feature) => normalize(feature).includes(normalizedSelected))
    );
  }, [accounts, selectedFeature]);

  const pageTitle =
    selectedFeature !== "all"
      ? FEATURE_PRESET_TITLES[selectedFeature] || selectedFeature
      : "Bank Account Features";

  return (
    <div className="p-6">
      <div className="max-w-[1100px] mx-auto space-y-6">
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl shadow-md p-3 md:p-4 space-y-1.5">
          <p className="text-[11px] uppercase tracking-wide text-blue-50">Bank Accounts</p>
          <h1 className="text-xl md:text-2xl font-semibold leading-snug">{pageTitle}</h1>
          <p className="text-sm text-blue-50/80 max-w-3xl">
            Explore bank accounts by feature and compare relevant options in one place.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSelectedFeature("all")}
            className={`px-3 py-1.5 rounded-full border text-sm ${
              selectedFeature === "all"
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-700 border-gray-300"
            }`}
          >
            All Features
          </button>
          {featureOptions.map((feature) => (
            <button
              key={feature}
              type="button"
              onClick={() => setSelectedFeature(feature)}
              className={`px-3 py-1.5 rounded-full border text-sm ${
                selectedFeature === feature
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-300"
              }`}
            >
              {feature}
            </button>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row justify-center items-start gap-6">
          <div className="w-full lg:w-72">
            <BankAccountFilter
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
            />
          </div>

          <div className="w-full flex-1 space-y-4">
            {filteredAccounts.length === 0 ? (
              <p className="text-gray-600">No bank accounts match the selected features and filters.</p>
            ) : (
              filteredAccounts.map((account) => (
                <BankAccountCard
                  key={account.id}
                  id={account.id}
                  bankLogo={account.bankLogo}
                  bankName={account.bankName}
                  accountName={account.accountName}
                  minBalance={account.minBalance}
                  minIncome={account.minIncome}
                  accountType={account.accountType}
                  features={account.features}
                  offers={account.offers}
                  accountUrl={account.accountUrl}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BankAccountFeaturesPage() {
  return (
    <Suspense fallback={<div className="p-6 text-center">Loading...</div>}>
      <BankAccountFeaturesContent />
    </Suspense>
  );
}
